import * as React from "react"

import { cn } from "../lib/utils"

// Content panels own an opaque card surface; callers customize layout, not chrome.
// Use a plain div for transparent canvases/inset groups, and `as` for semantic sections/articles.
const Card = React.forwardRef(({
  as: Component = "div",
  className,
  ...props
}, ref) => {
  return React.createElement(Component, {
    ref,
    "data-slot": "card",
    className: cn(
      "bg-card text-card-foreground flex flex-col gap-[var(--ds-space-panel)] rounded-[var(--ds-radius-surface)] border border-border py-[var(--ds-space-panel)] shadow-[var(--shadow-surface)] transition-colors",
      className,
    ),
    ...props,
  });
});
Card.displayName = "Card";

const CardHeader = React.forwardRef(({
  className,
  ...props
}, ref) => {
  return (
    <div
      ref={ref}
      data-slot="card-header"
      className={cn(
        "@container/card-header grid auto-rows-min grid-rows-[auto_auto] items-start gap-[var(--ds-space-2)] px-[var(--ds-space-6)] has-data-[slot=card-action]:grid-cols-[1fr_auto] [.border-b]:pb-[var(--ds-space-6)]",
        className
      )}
      {...props} />
  );
});
CardHeader.displayName = "CardHeader";

const CardTitle = React.forwardRef(({
  className,
  as = "div",
  ...props
}, ref) => {
  return React.createElement(as, {
    ref,
    "data-slot": "card-title",
    className: cn("leading-none font-semibold tracking-[-0.02em]", className),
    ...props,
  });
});
CardTitle.displayName = "CardTitle";

const CardDescription = React.forwardRef(({
  className,
  ...props
}, ref) => {
  return (
    <div
      ref={ref}
      data-slot="card-description"
      className={cn("text-muted-foreground text-sm leading-5", className)}
      {...props} />
  );
});
CardDescription.displayName = "CardDescription";

const CardAction = React.forwardRef(({
  className,
  ...props
}, ref) => {
  return (
    <div
      ref={ref}
      data-slot="card-action"
      className={cn(
        "col-start-2 row-span-2 row-start-1 self-start justify-self-end",
        className
      )}
      {...props} />
  );
});
CardAction.displayName = "CardAction";

const CardContent = React.forwardRef(({
  className,
  ...props
}, ref) => {
  return (<div ref={ref} data-slot="card-content" className={cn("px-[var(--ds-space-6)]", className)} {...props} />);
});
CardContent.displayName = "CardContent";

const CardFooter = React.forwardRef(({
  className,
  ...props
}, ref) => {
  return (
    <div
      ref={ref}
      data-slot="card-footer"
      className={cn("flex items-center px-[var(--ds-space-6)] [.border-t]:pt-[var(--ds-space-6)]", className)}
      {...props} />
  );
});
CardFooter.displayName = "CardFooter";

export {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardAction,
  CardDescription,
  CardContent,
}
